import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box, Chip, FormControl, IconButton, InputAdornment,
  InputLabel, MenuItem, Select, TextField, Tooltip, Typography,
} from "@material-ui/core";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import KanbanCard from "../../components/KanbanCard";
import ContactModal from "../../components/ContactModal";
import ScheduleModal from "../../components/ScheduleModal";
import ChatModal from "../../components/ChatModal";
import TicketTagsKanbanModal from "../../components/TicketTagsKanbanModal";
import KanbanTicketModal from "../../components/KanbanTicketModal";
import TicketNotesDrawer from "../../components/TicketNotesDrawer";
import EmailComposeModal from "../../components/EmailComposeModal";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    backgroundColor: "#f0f4f8",
  },
  topBar: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    padding: "10px 16px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #e2e8f0",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: { padding: "8px 12px", gap: 8 },
  },
  logoBox: {
    width: 34,
    height: 34,
    borderRadius: 9,
    flexShrink: 0,
    background: "linear-gradient(135deg, #6366f1, #818cf8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { fontSize: 18, color: "#fff" },
  },
  titleWrap: { flex: "1 1 120px", minWidth: 0 },
  pageTitle: { fontSize: 14, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 },
  pageSub: {
    fontSize: 10, color: "#94a3b8", marginTop: 1,
    [theme.breakpoints.down("xs")]: { display: "none" },
  },
  negocioCtrl: {
    minWidth: 190,
    flexShrink: 0,
    "& .MuiOutlinedInput-root": { borderRadius: 10, height: 38 },
    "& .MuiSelect-root": { paddingTop: 9, paddingBottom: 9, fontSize: 14, fontWeight: 600 },
    [theme.breakpoints.down("xs")]: { flex: "1 1 100%", minWidth: 0, order: 10 },
  },
  filterBar: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 20px",
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { padding: "6px 12px", gap: 6 },
  },
  searchField: {
    flex: "1 1 180px",
    minWidth: 110,
    "& .MuiOutlinedInput-root": { borderRadius: 8, height: 34, backgroundColor: "#fff" },
    "& .MuiInputBase-input": { padding: "7px 8px 7px 2px", fontSize: 13 },
  },
  filterCtrl: {
    flex: "1 1 130px",
    minWidth: 100,
    "& .MuiOutlinedInput-root": { borderRadius: 8, backgroundColor: "#fff", height: 36 },
    "& .MuiSelect-root": { paddingTop: 8, paddingBottom: 8, fontSize: 13 },
    "& .MuiInputLabel-outlined": { transform: "translate(12px, 10px) scale(1)", fontSize: 13 },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": { transform: "translate(12px, -5px) scale(0.72)" },
    [theme.breakpoints.down("xs")]: { flex: "1 1 100%", minWidth: 0 },
  },
  clearChip: {
    height: 26,
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 20,
    backgroundColor: "#eef2ff",
    color: "#4f46e5",
    border: "1px solid #c7d2fe",
    "& .MuiChip-deleteIcon": { color: "#4f46e5" },
  },
  /* board rola horizontalmente; altura cresce com o conteúdo (página rola verticalmente) */
  board: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    overflowX: "auto",
    overflowY: "visible",
    padding: "12px 14px 24px",
    gap: 10,
    "&::-webkit-scrollbar": { height: 8 },
    "&::-webkit-scrollbar-thumb": { background: "#94a3b8", borderRadius: 4 },
    "&::-webkit-scrollbar-track": { background: "#e2e8f0", borderRadius: 4 },
  },
  /* coluna cresce com os cards — sem altura máxima */
  lane: {
    width: 252,
    minWidth: 252,
    [theme.breakpoints.down("xs")]: { width: 220, minWidth: 220 },
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    boxSizing: "border-box",
    transition: "box-shadow .15s",
    overflow: "hidden",
  },
  laneOver: {
    boxShadow: "0 0 0 2px #6366f1, 0 4px 16px rgba(99,102,241,0.14)",
  },
  laneColumnTarget: {
    boxShadow: "0 0 0 2px #f59e0b, 0 4px 16px rgba(245,158,11,0.18)",
  },
  laneDragging: {
    opacity: 0.45,
    transform: "scale(0.98)",
  },
  laneHead: {
    flexShrink: 0,
    padding: "9px 10px 8px 8px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "flex-start",
    gap: 4,
  },
  dragHandle: {
    flexShrink: 0,
    color: "#d1d5db",
    cursor: "grab",
    paddingTop: 1,
    display: "flex",
    alignItems: "center",
    borderRadius: 4,
    "&:hover": { color: "#6366f1", backgroundColor: "#eef2ff" },
    "&:active": { cursor: "grabbing" },
    transition: "color .15s, background .15s",
  },
  laneHeadInfo: { flex: 1, minWidth: 0 },
  laneTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1.25,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  laneSub: { fontSize: 11, color: "#94a3b8", marginTop: 2 },
  /* corpo da coluna: sem scroll interno, a página rola verticalmente */
  laneBody: {
    padding: 8,
  },
  laneEmpty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 32,
    color: "#cbd5e1",
    fontSize: 12,
    fontStyle: "italic",
  },
  /* tela inicial sem funil selecionado */
  landing: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    color: "#94a3b8",
    userSelect: "none",
  },
  landingIcon: { fontSize: 64, color: "#e2e8f0" },
  landingTitle: { fontSize: 18, fontWeight: 700, color: "#64748b" },
  landingHint: { fontSize: 13, color: "#94a3b8", textAlign: "center", maxWidth: 320 },
}));

function fmtBRL(v) {
  return Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function Kanban() {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [selectedNegocioId, setSelectedNegocioId] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [connFilter, setConnFilter] = useState("");
  const [queueFilter, setQueueFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [connections, setConnections] = useState([]);
  const [agents, setAgents] = useState([]);
  const [dragOverLane, setDragOverLane] = useState(null);
  const [draggingLaneId, setDraggingLaneId] = useState(null);
  const [columnDropTarget, setColumnDropTarget] = useState(null);
  const isDraggingLane = useRef(false);

  const [ticketDetailOpen, setTicketDetailOpen] = useState(false);
  const [ticketDetailData, setTicketDetailData] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactId, setContactId] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleContact, setScheduleContact] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMeta, setChatMeta] = useState(null);
  const [chatUuid, setChatUuid] = useState(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [tagsTicket, setTagsTicket] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTicket, setHistoryTicket] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTicket, setEmailTicket] = useState(null);

  const socketDebounce = useRef(0);
  const boardRef = useRef(null);
  /* contador por lane para evitar que onDragLeave dispare ao passar sobre elementos filhos */
  const dragEnterCount = useRef({});

  var fetchTags = function() {
    api.get("/tag/kanban").then(function(res) {
      var data = res.data;
      setTags(Array.isArray(data.lista) ? data.lista : []);
    }).catch(function() { setTags([]); });
  };

  var fetchTickets = function() {
    api.get("/ticket/kanban").then(function(res) {
      var data = res.data;
      setTickets(Array.isArray(data.tickets) ? data.tickets : []);
    }).catch(function() { setTickets([]); });
  };

  var fetchNegocios = function() {
    api.get("/negocios").then(function(res) {
      var d = res.data;
      var lista = Array.isArray(d) ? d : (d && d.list ? d.list : []);
      setNegocios(lista);
      if (lista.length > 0) {
        setSelectedNegocioId(function(prev) { return prev || String(lista[0].id); });
      }
    }).catch(function() { setNegocios([]); });
  };

  var fetchConnections = function() {
    api.get("/whatsapp").then(function(res) {
      var d = res.data;
      setConnections(Array.isArray(d) ? d : (d && d.whatsapps ? d.whatsapps : []));
    }).catch(function() { setConnections([]); });
  };

  var fetchAgents = function() {
    api.get("/users").then(function(res) {
      var d = res.data;
      setAgents(d && Array.isArray(d.users) ? d.users : []);
    }).catch(function() { setAgents([]); });
  };

  useEffect(function() {
    fetchTags();
    fetchTickets();
    fetchNegocios();
    fetchConnections();
    fetchAgents();
  }, []);

  useEffect(function() {
    var cid = user.companyId;
    var handler = function(data) {
      var now = Date.now();
      if (now - socketDebounce.current < 1500) return;
      socketDebounce.current = now;
      if (data.action === "create" || data.action === "update") fetchTickets();
    };
    socket.on("company-" + cid + "-ticket", handler);
    socket.on("company-" + cid + "-appMessage", handler);
    return function() {
      socket.off("company-" + cid + "-ticket", handler);
      socket.off("company-" + cid + "-appMessage", handler);
    };
  }, [socket]);

  var selectedNegocio = useMemo(function() {
    if (!selectedNegocioId) return null;
    return negocios.find(function(n) { return String(n.id) === String(selectedNegocioId); }) || null;
  }, [negocios, selectedNegocioId]);

  /* tags ordenadas pelo funil selecionado — vazia se nenhum funil */
  var orderedTags = useMemo(function() {
    if (!selectedNegocio) return [];
    var boards = selectedNegocio.kanbanBoards;
    if (!boards || !boards.length) return tags;
    var order = boards.map(String);
    var map = new Map(tags.map(function(t) { return [String(t.id), t]; }));
    return order.map(function(id) { return map.get(id); }).filter(Boolean);
  }, [tags, selectedNegocio]);

  var lanes = useMemo(function() {
    if (!orderedTags.length) return [];
    var filtered = tickets.filter(function(t) {
      var c = t.contact || {};
      if (search) {
        var q = search.toLowerCase();
        var n = (c.name || "").toLowerCase();
        var p = (c.number || "").replace(/\D/g, "");
        if (n.indexOf(q) === -1 && p.indexOf(search.replace(/\D/g, "")) === -1) return false;
      }
      if (connFilter && String(t.whatsappId) !== String(connFilter)) return false;
      if (queueFilter && String(t.queueId) !== String(queueFilter)) return false;
      if (userFilter && String(t.userId) !== String(userFilter)) return false;
      return true;
    });
    return orderedTags.map(function(tag) {
      var lts = filtered.filter(function(t) {
        return (t.tags || []).some(function(tg) { return tg.id === tag.id; });
      });
      return {
        id: tag.id,
        name: tag.name,
        color: tag.color || "#6366f1",
        count: lts.length,
        value: lts.reduce(function(s, t) { return s + (Number(t.leadValue) || 0); }, 0),
        tickets: lts,
      };
    });
  }, [orderedTags, tickets, search, connFilter, queueFilter, userFilter]);

  /* ── drag de CARD ─────────────────────────────── */
  var onCardDragStart = function(e, cardId, laneId) {
    isDraggingLane.current = false;
    e.dataTransfer.setData("cardId", String(cardId));
    e.dataTransfer.setData("fromLane", String(laneId));
    e.dataTransfer.effectAllowed = "move";
    var ghost = document.createElement("div");
    ghost.textContent = "Mover ticket";
    ghost.style.cssText = "position:fixed;top:-200px;left:-200px;padding:8px 18px;border-radius:20px;background:#6366f1;color:#fff;font:600 13px/1.4 sans-serif;box-shadow:0 4px 12px rgba(99,102,241,0.4);white-space:nowrap;";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, 20);
    setTimeout(function() { document.body.removeChild(ghost); }, 0);
  };

  var onDragEnter = function(e, laneId) {
    e.preventDefault();
    if (isDraggingLane.current) {
      setColumnDropTarget(laneId);
      return;
    }
    dragEnterCount.current[laneId] = (dragEnterCount.current[laneId] || 0) + 1;
    if (dragEnterCount.current[laneId] === 1) setDragOverLane(laneId);
  };

  var onDragOver = function(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  var onDragLeave = function(laneId) {
    if (isDraggingLane.current) {
      setColumnDropTarget(null);
      return;
    }
    dragEnterCount.current[laneId] = Math.max(0, (dragEnterCount.current[laneId] || 0) - 1);
    if (dragEnterCount.current[laneId] === 0) setDragOverLane(null);
  };

  var onDrop = function(e, toLaneId) {
    e.preventDefault();
    dragEnterCount.current[toLaneId] = 0;
    setDragOverLane(null);
    setColumnDropTarget(null);

    var draggedLaneId = e.dataTransfer.getData("laneId");
    var cardId = Number(e.dataTransfer.getData("cardId"));

    /* drop de COLUNA */
    if (draggedLaneId) {
      isDraggingLane.current = false;
      setDraggingLaneId(null);
      var fromId = Number(draggedLaneId);
      if (!fromId || fromId === toLaneId) return;
      var currentOrder = orderedTags.map(function(t) { return t.id; });
      var fi = currentOrder.indexOf(fromId);
      var ti = currentOrder.indexOf(toLaneId);
      if (fi === -1 || ti === -1) return;
      var newOrder = currentOrder.slice();
      newOrder.splice(fi, 1);
      newOrder.splice(ti, 0, fromId);
      /* otimista */
      setNegocios(function(prev) {
        return prev.map(function(n) {
          if (String(n.id) !== String(selectedNegocioId)) return n;
          return Object.assign({}, n, { kanbanBoards: newOrder });
        });
      });
      api.put("/negocios/" + selectedNegocioId, { kanbanBoards: newOrder })
        .catch(function() { fetchNegocios(); });
      return;
    }

    /* drop de CARD */
    var fromLaneId = e.dataTransfer.getData("fromLane");
    if (!cardId || String(fromLaneId) === String(toLaneId)) return;
    var toTag = orderedTags.find(function(t) { return String(t.id) === String(toLaneId); });
    if (toTag) {
      var kanbanIds = new Set(orderedTags.map(function(t) { return t.id; }));
      setTickets(function(prev) {
        return prev.map(function(t) {
          if (t.id !== cardId) return t;
          var otherTags = (t.tags || []).filter(function(tg) { return !kanbanIds.has(tg.id); });
          return Object.assign({}, t, { tags: otherTags.concat([toTag]) });
        });
      });
    }
    api.delete("/ticket-tags/" + cardId).then(function() {
      return api.put("/ticket-tags/" + cardId + "/" + toLaneId);
    }).then(function() {
      fetchTickets();
    }).catch(function() {
      toast.error("Erro ao mover ticket");
      fetchTickets();
    });
  };

  /* ── drag de COLUNA ────────────────────────────── */
  var onColumnDragStart = function(e, laneId, laneName) {
    e.stopPropagation();
    isDraggingLane.current = true;
    setDraggingLaneId(laneId);
    e.dataTransfer.setData("laneId", String(laneId));
    e.dataTransfer.effectAllowed = "move";
    var ghost = document.createElement("div");
    ghost.textContent = laneName || "Mover coluna";
    ghost.style.cssText = "position:fixed;top:-200px;left:-200px;padding:6px 14px;border-radius:20px;background:#f59e0b;color:#fff;font:700 12px/1.4 sans-serif;box-shadow:0 4px 12px rgba(245,158,11,0.4);white-space:nowrap;";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, 18);
    setTimeout(function() { document.body.removeChild(ghost); }, 0);
  };

  var onColumnDragEnd = function() {
    isDraggingLane.current = false;
    setDraggingLaneId(null);
    setColumnDropTarget(null);
  };

  var openTicketDetail = function(t) { setTicketDetailData(t); setTicketDetailOpen(true); };
  var closeTicketDetail = function() { setTicketDetailOpen(false); setTicketDetailData(null); };
  var openContact = function(id) { setContactId(id); setContactOpen(true); };
  var closeContact = function() { setContactOpen(false); setContactId(null); };
  var openSchedule = function(id) { setScheduleContact(id); setScheduleOpen(true); };
  var closeSchedule = function() { setScheduleOpen(false); setScheduleContact(null); };
  var openChat = function(ticket) {
    var c = ticket.contact || {};
    setChatMeta({ number: c.number, name: c.name, whatsappId: ticket.whatsappId });
    setChatUuid(ticket.uuid);
    setChatOpen(true);
  };
  var closeChat = function() { setChatOpen(false); setChatMeta(null); setChatUuid(null); };
  var openTags = function(ticket) { setTagsTicket(ticket); setTagsOpen(true); };
  var closeTags = function() { setTagsOpen(false); setTagsTicket(null); };
  var openHistory = function(ticket) { setHistoryTicket(ticket); setHistoryOpen(true); };
  var closeHistory = function() { setHistoryOpen(false); setHistoryTicket(null); };
  var openEmail = function(ticket) { setEmailTicket(ticket); setEmailOpen(true); };
  var closeEmail = function() { setEmailOpen(false); setEmailTicket(null); };

  var activeCount = [search, connFilter, queueFilter, userFilter].filter(Boolean).length;
  var clearAll = function() { setSearch(""); setConnFilter(""); setQueueFilter(""); setUserFilter(""); };
  var userQueues = user.queues || [];

  var totalTickets = lanes.reduce(function(s, l) { return s + l.count; }, 0);

  return (
    <div className={classes.root}>

      {/* barra superior */}
      <Box className={classes.topBar}>
        <Box className={classes.logoBox}><ViewColumnIcon /></Box>
        <Box className={classes.titleWrap}>
          <Typography className={classes.pageTitle}>Kanban</Typography>
          {selectedNegocio && (
            <Typography className={classes.pageSub}>
              {selectedNegocio.name}
              {" · "}
              {totalTickets} ticket{totalTickets !== 1 ? "s" : ""}
              {" · "}
              {lanes.length} etapa{lanes.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>
        <Tooltip title={showFilters ? "Ocultar filtros" : "Mostrar filtros"}>
          <IconButton
            size="small"
            onClick={function() { setShowFilters(function(v) { return !v; }); }}
            style={{ marginRight: 4 }}
          >
            <FilterListIcon style={{ color: showFilters ? "#6366f1" : "#94a3b8", fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <FormControl variant="outlined" size="small" className={classes.negocioCtrl}>
          <Select
            value={selectedNegocioId}
            onChange={function(e) { setSelectedNegocioId(e.target.value); }}
            displayEmpty
            renderValue={function(v) {
              if (!v) return <span style={{ color: "#94a3b8", fontWeight: 400 }}>Selecionar funil</span>;
              var neg = negocios.find(function(n) { return String(n.id) === String(v); });
              return neg ? neg.name : "Funil";
            }}
          >
            {negocios.map(function(n) {
              return <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>

      {/* barra de filtros (opcional) */}
      {showFilters && (
        <Box className={classes.filterBar}>
          <TextField
            placeholder="Buscar contato ou número..."
            value={search}
            onChange={function(e) { setSearch(e.target.value); }}
            variant="outlined"
            size="small"
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 15, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl variant="outlined" size="small" className={classes.filterCtrl}>
            <InputLabel>Conexão</InputLabel>
            <Select
              value={connFilter}
              onChange={function(e) { setConnFilter(e.target.value); }}
              label="Conexão"
            >
              <MenuItem value="">Todas</MenuItem>
              {connections.map(function(c) {
                return <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterCtrl}>
            <InputLabel>Fila</InputLabel>
            <Select
              value={queueFilter}
              onChange={function(e) { setQueueFilter(e.target.value); }}
              label="Fila"
            >
              <MenuItem value="">Todas</MenuItem>
              {userQueues.map(function(q) {
                return <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterCtrl}>
            <InputLabel>Atendente</InputLabel>
            <Select
              value={userFilter}
              onChange={function(e) { setUserFilter(e.target.value); }}
              label="Atendente"
            >
              <MenuItem value="">Todos</MenuItem>
              {agents.map(function(a) {
                return <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>;
              })}
            </Select>
          </FormControl>
          {activeCount > 0 && (
            <Chip
              label={"Limpar (" + activeCount + ")"}
              size="small"
              onDelete={clearAll}
              onClick={clearAll}
              className={classes.clearChip}
            />
          )}
        </Box>
      )}

      {/* área principal */}
      {!selectedNegocioId ? (

        /* landing: nenhum funil selecionado */
        <Box className={classes.landing}>
          <ViewColumnIcon className={classes.landingIcon} />
          <Typography className={classes.landingTitle}>Selecione um funil</Typography>
          <Typography className={classes.landingHint}>
            Escolha um funil no seletor acima para visualizar os tickets organizados por etapas do Kanban.
          </Typography>
        </Box>

      ) : (

        /* board de colunas */
        <Box ref={boardRef} className={classes.board}>
          {lanes.length === 0 ? (
            <Box className={classes.landing}>
              <ViewColumnIcon className={classes.landingIcon} />
              <Typography className={classes.landingTitle}>Nenhuma etapa configurada</Typography>
              <Typography className={classes.landingHint}>
                Este funil não possui etapas Kanban. Configure os quadros do negócio para começar.
              </Typography>
            </Box>
          ) : (
            lanes.map(function(lane) {
              var isCardOver = dragOverLane === lane.id;
              var isColTarget = columnDropTarget === lane.id && draggingLaneId !== lane.id;
              var isBeingDragged = draggingLaneId === lane.id;
              var cls = [
                classes.lane,
                isCardOver ? classes.laneOver : "",
                isColTarget ? classes.laneColumnTarget : "",
                isBeingDragged ? classes.laneDragging : "",
              ].filter(Boolean).join(" ");
              return (
                <Box
                  key={lane.id}
                  className={cls}
                  style={{ borderTop: "4px solid " + lane.color }}
                  onDragEnter={function(e) { onDragEnter(e, lane.id); }}
                  onDragOver={onDragOver}
                  onDragLeave={function() { onDragLeave(lane.id); }}
                  onDrop={function(e) { onDrop(e, lane.id); }}
                >
                  <Box className={classes.laneHead}>
                    <Tooltip title="Arrastar para reordenar coluna">
                      <Box
                        className={classes.dragHandle}
                        draggable
                        onDragStart={function(e) { onColumnDragStart(e, lane.id, lane.name); }}
                        onDragEnd={onColumnDragEnd}
                      >
                        <DragIndicatorIcon style={{ fontSize: 16 }} />
                      </Box>
                    </Tooltip>
                    <Box className={classes.laneHeadInfo}>
                      <Typography className={classes.laneTitle} title={lane.name}>
                        {lane.name}
                      </Typography>
                      <Typography className={classes.laneSub}>
                        {lane.count} ticket{lane.count !== 1 ? "s" : ""}
                        {" · "}
                        {fmtBRL(lane.value)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={classes.laneBody}>
                    {lane.tickets.length === 0 ? (
                      <Box className={classes.laneEmpty}>Nenhum ticket nesta etapa</Box>
                    ) : (
                      lane.tickets.map(function(ticket) {
                        return (
                          <Box
                            key={ticket.id}
                            draggable
                            onDragStart={function(e) { onCardDragStart(e, ticket.id, lane.id); }}
                            style={{ cursor: "grab" }}
                          >
                            <KanbanCard
                              ticket={ticket}
                              onOpenTicketDetail={openTicketDetail}
                              onOpenSchedule={openSchedule}
                              onOpenChat={openChat}
                              onOpenDetails={openTags}
                              onOpenEmail={openEmail}
                              onOpenHistory={openHistory}
                            />
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      )}

      <KanbanTicketModal open={ticketDetailOpen} onClose={closeTicketDetail} ticket={ticketDetailData} />
      <ContactModal open={contactOpen} onClose={closeContact} contactId={contactId} />
      <ScheduleModal open={scheduleOpen} onClose={closeSchedule} contactId={scheduleContact} />
      <ChatModal open={chatOpen} onClose={closeChat} contact={chatMeta} ticketUuid={chatUuid} />
      <TicketTagsKanbanModal
        open={tagsOpen}
        onClose={closeTags}
        contact={tagsTicket && tagsTicket.contact}
        ticket={tagsTicket}
        onUpdate={fetchTickets}
      />
      <TicketNotesDrawer
        open={historyOpen}
        onClose={closeHistory}
        ticket={historyTicket}
      />
      <EmailComposeModal
        open={emailOpen}
        onClose={closeEmail}
        initialTo={emailTicket && emailTicket.contact && emailTicket.contact.email ? emailTicket.contact.email : ""}
        initialSubject={"Atendimento #" + (emailTicket ? emailTicket.id : "")}
        onSent={closeEmail}
      />
    </div>
  );
}